'use client'

import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import type { LucideIcon } from 'lucide-react'

interface Capability {
  label: string
  message: string
  icon: LucideIcon
}

const CAPABILITIES: Capability[] = [
  {
    label: 'Show my open tickets',
    message: 'Show me all currently open support tickets',
    icon: Icons.fileText,
  },
  {
    label: 'Last 24 hours activity',
    message: 'What happened in the AuthID platform in the last 24 hours?',
    icon: Icons.clock,
  },
  {
    label: 'Generate verification report',
    message: 'Generate a verifications report for the last 30 days',
    icon: Icons.barChart,
  },
  {
    label: 'Search for a customer issue',
    message: 'Search for recent tickets related to passport scan rejection',
    icon: Icons.search,
  },
  {
    label: 'Explain this page',
    message: 'Explain what this page does and how to use it',
    icon: Icons.helpCircle,
  },
  {
    label: 'What can you help with?',
    message: 'What can you help me with?',
    icon: Icons.sparkles,
  },
  {
    label: 'Create a PDF summary',
    message: 'Create a PDF summary of the current platform status and open ticket backlog',
    icon: Icons.fileText,
  },
  {
    label: 'Search the web',
    message: 'Search Google for AuthID document verification best practices',
    icon: Icons.globe,
  },
]

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: 'easeOut' } },
}

interface CapabilityBubblesProps {
  onSelect: (message: string) => void
}

export function CapabilityBubbles({ onSelect }: CapabilityBubblesProps) {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-wrap justify-center gap-2"
      role="list"
      aria-label="Quick action suggestions"
    >
      {CAPABILITIES.map(cap => {
        const IconComponent = cap.icon
        return (
          <motion.button
            key={cap.label}
            variants={itemVariants}
            onClick={() => onSelect(cap.message)}
            role="listitem"
            aria-label={`Quick action: ${cap.label}`}
            className={cn(
              'inline-flex items-center gap-2 rounded-full px-3.5 py-2',
              'text-xs font-medium transition-all duration-150',
              'border border-brand-cornflower/25 bg-brand-cornflower/5',
              'text-brand-navy hover:bg-brand-cornflower/15 hover:border-brand-cornflower/50',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower/60',
              'cursor-pointer select-none',
            )}
          >
            {IconComponent && (
              <IconComponent
                className="h-3.5 w-3.5 flex-shrink-0 text-brand-cornflower"
                strokeWidth={1.5}
                aria-hidden="true"
              />
            )}
            {cap.label}
          </motion.button>
        )
      })}
    </motion.div>
  )
}
